// Справка „Променени цени" (Справки → price-changes).
//
// Кой, кога и на кой ред е сменил цената. Периодът е по МОМЕНТА НА СМЯНАТА,
// затова справката лови и още отворени маси.
// `GET /reports/analytics/price-changes` връща целия период наведнъж (масив,
// без страниране), затова таблицата работи в КЛИЕНТСКИ режим: сортирането,
// филтрите в колоните и редът „Общо" се смятат в браузъра, а Excel изнася
// видяното.
//
// БЕЗ бутон „Δ спрямо предходния период": справката не пита втори период и
// кутийките нямат делта — сравнението „с колко повече цени са сменени от
// вчера" не отговаря на въпроса, заради който се отваря този екран.

import { useCallback, useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import {
  usePriceChangesReport,
  useBillHistoryById,
} from "../../../hooks/useReports";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import type { ChecklistOption } from "../../../components/dataTable";
import ExportExcelButton from "./ExportExcelButton";
import { BillOperationsModal } from "./BillOperationsModal";
import { ReportTopBar } from "./ReportTopBar";
import { pluralLabel } from "./reportPlural";
import { PriceChangesKpis } from "./priceChanges/PriceChangesKpis";
import { PriceChangeMobileCard } from "./priceChanges/PriceChangeMobileCard";
import { usePriceChangesColumns } from "./priceChanges/priceChangesColumns";
import { exportPriceChanges } from "./priceChanges/priceChangesExport";
import {
  buildPriceChangeRows,
  summarizePriceChanges,
} from "./priceChanges/priceChangesMath";
import type { PriceChangeRow } from "./priceChanges/priceChangesMath";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const PriceChangesReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;

  // Датата живее и в query параметъра ?date= — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  const [searchText, setSearchText] = useState("");
  const [selectedBillId, setSelectedBillId] = useState<number | null>(null);
  // Редовете СЛЕД филтър и подредба — вдигат се от таблицата, защото само тя
  // знае какво е останало след филтрите в колоните. `null` = таблицата още не
  // се е обадила; дотогава показателите четат целия набор, за да не мигне
  // нула на първия кадър.
  const [visibleRows, setVisibleRows] = useState<PriceChangeRow[] | null>(null);

  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      start_date: dateRange[0].format("YYYY-MM-DD HH:mm:ss"),
      end_date: dateRange[1].format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [dateRange]);

  const {
    data: items = [],
    isLoading,
    isFetching,
    refetch,
  } = usePriceChangesReport(filters);

  const { data: selectedBill } = useBillHistoryById(selectedBillId);
  const isModalOpen =
    selectedBillId !== null && selectedBill?.id === selectedBillId;

  const labels = useMemo(
    () => ({
      product: t("reportsPage.priceChangesKindProduct"),
      addition: t("reportsPage.priceChangesKindAddition"),
      up: t("reportsPage.priceChangesUp"),
      down: t("reportsPage.priceChangesDown"),
      active: t("reportsPage.priceChangesActive"),
      reverted: t("reportsPage.priceChangesReverted"),
    }),
    [t],
  );

  const rows = useMemo(
    () => buildPriceChangeRows(items, labels),
    [items, labels],
  );

  // Търсачката гледа това, което човек чете в реда: продукт, маса (и номерът
  // ѝ) и кой е сменил цената. Стесняването по един артикул или потребител си
  // остава в чеклиста на колоната.
  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [row.itemLabel, row.billLabel, `#${row.billId}`, row.userLabel]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [rows, searchText]);

  // Опциите на чеклистите се строят от ЦЕЛИЯ отговор, не от оцелелите след
  // филтър редове — иначе стойностите изчезват, докато стесняваш избора.
  const userOptions = useMemo<ChecklistOption[]>(() => {
    const seen = new Set<string>();
    for (const row of rows) if (row.userLabel) seen.add(row.userLabel);
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((label) => ({ value: label, label }));
  }, [rows]);

  const itemOptions = useMemo<ChecklistOption[]>(() => {
    const seen = new Set<string>();
    for (const row of rows) seen.add(row.itemLabel);
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((label) => ({ value: label, label }));
  }, [rows]);

  const isMultiDay = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return false;
    return !dateRange[0].isSame(dateRange[1], "day");
  }, [dateRange]);

  const columns = usePriceChangesColumns({
    isMultiDay,
    userOptions,
    itemOptions,
  });

  // Кутийките, броячът в заглавието и Excel четат ЕДИН И СЪЩ набор.
  const shownRows = visibleRows ?? searchedRows;
  const summary = useMemo(() => summarizePriceChanges(shownRows), [shownRows]);

  // Стабилна функция — иначе ефектът в таблицата се пуска на всеки рендер.
  const handleVisibleRows = useCallback(
    (next: PriceChangeRow[]) => setVisibleRows(next),
    [],
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.priceChangesTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <PriceChangesKpis summary={summary} isNarrow={narrow} />

              <DataTable<PriceChangeRow>
                tableId="price-changes"
                data={searchedRows}
                columns={columns}
                getRowId={(row) => row.rowId}
                refreshing={isFetching}
                ariaLabel={t("reportsPage.priceChangesTitle")}
                title={
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                      {t("reportsPage.priceChangesListKicker")}
                    </span>
                    <span className="text-[16px] font-extrabold leading-none text-ink">
                      {pluralLabel(
                        t,
                        shownRows.length,
                        "reportsPage.priceChangesResultOne",
                        "reportsPage.priceChangesResults",
                      )}
                      <span className="font-medium text-muted">
                        {` · ${t("reportsPage.priceChangesTableHint")}`}
                      </span>
                    </span>
                  </div>
                }
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.priceChangesSearchPlaceholder"),
                }}
                onFilteredRowsChange={handleVisibleRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={() => exportPriceChanges(shownRows, { t })}
                    disabled={shownRows.length === 0}
                    bare
                  />
                }
                onRowClick={(row) => setSelectedBillId(row.billId)}
                mobileCard={(row) => <PriceChangeMobileCard row={row} />}
                emptyTitle={t("reportsPage.priceChangesNoData")}
                emptyHint={t("reportsPage.priceChangesEmptyHint")}
                initialSorting={[{ id: "changed_at", desc: true }]}
              />
            </>
          )}
        </div>
      </div>

      <BillOperationsModal
        bill={selectedBill ?? null}
        open={isModalOpen}
        onClose={() => setSelectedBillId(null)}
        onBillUpdated={() => refetch()}
      />
    </div>
  );
};
